import './store-designer-v2.css';
import {setCityDirection} from '../i18n';
import {cityRenderResolution,isMobileGPU} from '../core/renderQuality';
import {Application,Container,Graphics} from 'pixi.js';
import {CityApi} from '../api/client';
import type {CityAsset,HouseData} from '../api/types';
import {IsoProjection} from '../core/projection';
import {House} from '../houses/House';
import {StoreAvatarEditor} from './StoreAvatarEditor';
import {StorefrontControls} from '../storefronts/StorefrontControls';
import {resolveDesign,type StorefrontDesign} from '../storefronts/model';
import {loadKit} from '../storefronts/catalog';
const deepClone=structuredClone;

/** One shop editor. Legacy data is imported before entering this workspace. */
export class StoreDesigner {
 private readonly root=document.createElement('div');
 private readonly previewHost=document.createElement('div');
 private readonly previewControls=document.createElement('div');
 private previewZoom=1;
 private readonly notice=document.createElement('div');
 private readonly nameInput=document.createElement('input');
 private readonly descriptionInput=document.createElement('textarea');
 private readonly openFrom=document.createElement('input');
 private readonly openUntil=document.createElement('input');
 private readonly avatarEditor:StoreAvatarEditor;
 private readonly modularControls:StorefrontControls;
 private draftModular:StorefrontDesign|null=null;
 private draftPlacement:HouseData['placement']|null=null;
 private draftStore:Record<string,unknown>={};
 private house:HouseData|null=null;
 private previewApp:Application|null=null;
 private previewWorld:Container|null=null;
 private previewSetup:Promise<void>|null=null;
 private previewVersion=0;
 private previewNeedsRebuild=true;
 private previewTimer=0;
 private openVersion=0;
 private disposed=false;
 private savedFingerprint='';
 private returnFocus:HTMLElement|null=null;
 private readonly onKey=(event:KeyboardEvent)=>{
  if(this.root.hidden)return;
  if(event.key==='Escape'){event.preventDefault();this.close();return;}
  if(event.key==='Tab'){
   const controls=[...this.root.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]')].filter(node=>node.getClientRects().length>0);
   const first=controls[0],last=controls.at(-1);if(!first||!last)return;
   if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}
   else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
  }
 };
 private fingerprint():string{return JSON.stringify([this.nameInput.value,this.descriptionInput.value,this.openFrom.value,this.openUntil.value,this.draftModular,this.avatarEditor.getCurrent()]);}
 private readonly previewResize=new ResizeObserver(()=>{if(!this.root.hidden&&this.previewHost.clientWidth>0)this.queuePreview(false);});

 constructor(private readonly api:CityApi,private readonly assets:CityAsset[],private readonly onOpenPlans:(house:HouseData)=>void,private readonly isAdmin=false,private readonly onAvatarChanged:(house:HouseData)=>void=()=>{},private readonly onSaved:(house:HouseData)=>void=()=>{},private readonly onVisibilityChange:(visible:boolean)=>void=()=>{}){
  this.root.className='city-modal city-store-designer city-studio-v2 is-modular';
  this.root.hidden=true;setCityDirection(this.root);this.root.setAttribute('role','dialog');
  this.root.setAttribute('aria-modal','true');this.root.setAttribute('aria-label','کارگاه شخصی‌سازی مغازه');
  this.modularControls=new StorefrontControls(design=>{this.draftModular=design;this.queuePreview();});
  const panel=document.createElement('section');panel.className='city-modal__panel city-store-designer__panel';
  panel.innerHTML=[
    '<header class="city-studio-v2__header"><div class="city-studio-v2__identity"><span>KEYSTONE CITY / DESIGN LAB</span><h1>کارگاه شخصی‌سازی</h1><p>مغازه‌ات را بساز، آواتارت را انتخاب کن و نتیجه را همان‌جا ببین.</p></div><button class="city-studio-v2__close" data-studio-close type="button" aria-label="بستن کارگاه">×</button></header>',
    '<nav class="city-studio-v2__tabs" aria-label="بخش‌های شخصی‌سازی"><button type="button" data-studio-tab="shop" aria-pressed="true">نمای مغازه</button><button type="button" data-studio-tab="avatar" aria-pressed="false">آواتار من</button><button type="button" data-studio-tab="details" aria-pressed="false">اطلاعات مغازه</button></nav>',
    '<div class="city-store-designer__body city-studio-v2__body"><main class="city-store-designer__center city-studio-v2__stage"><div class="city-studio-v2__stage-label">پیش‌نمایش زنده · شهر کیستون</div><div class="city-studio-v2__stage-hint">از ابزارهای سمت چپ یک گزینه انتخاب کن و نتیجه را ببین.</div><div class="city-studio-v2__portrait"></div></main><section class="city-studio-v2__dock" aria-label="ابزار شخصی‌سازی"><div class="city-studio-v2__dock-heading"><strong>انتخاب و شخصی‌سازی</strong><span>نتیجهٔ تغییرات روی صحنه دیده می‌شود</span></div><div class="city-studio-v2__pane" data-studio-pane="shop"></div><div class="city-studio-v2__pane" data-studio-pane="avatar"></div><div class="city-studio-v2__pane city-studio-v2__fields" data-studio-pane="details"></div></section></div>',
    '<footer class="city-studio-v2__footer"><button type="button" data-studio-plans class="city-btn city-studio-v2__secondary">طراحی ویترین و داخل مغازه</button><button type="button" data-studio-save class="city-btn city-btn--primary city-studio-v2__save">ذخیره تغییرات</button></footer>'
  ].join('');
  const get=(q:string)=>panel.querySelector<HTMLElement>(q)!;
  get('[data-studio-close]').onclick=()=>this.close();
  for(const button of panel.querySelectorAll<HTMLButtonElement>('[data-studio-tab]')){
    button.onclick=()=>this.switchTab(button.dataset.studioTab as 'shop'|'avatar'|'details');
  }
  const center=get('.city-studio-v2__stage');
  this.previewHost.className='city-store-designer__preview';center.prepend(this.previewHost);
  this.notice.className='city-modal__notice city-studio-v2__notice';this.notice.hidden=true;center.append(this.notice);
  this.previewControls.className='city-studio-zoom city-studio-v2__zoom';
  for(const [text,label,factor] of [['+','بزرگ‌نمایی',1.3],['−','کوچک‌نمایی',1/1.3],['⌖','نمای کامل',0]] as const){
    const button=document.createElement('button');button.type='button';button.textContent=text;
    button.setAttribute('aria-label',label);button.title=label;
    button.onclick=()=>{this.previewZoom=factor?Math.max(.5,Math.min(5,this.previewZoom*factor)):1;this.queuePreview(false);};
    this.previewControls.append(button);
  }
  get('[data-studio-pane="shop"]').append(this.modularControls.element);
  const details=get('[data-studio-pane="details"]');
  const field=(label:string,input:HTMLElement)=>{
    const wrap=document.createElement('label');const span=document.createElement('span');
    span.textContent=label;wrap.append(span,input);return wrap;
  };
  this.nameInput.setAttribute('aria-label','نام کسب‌وکار');this.descriptionInput.rows=2;
  this.nameInput.dir='auto';this.descriptionInput.dir='auto';
  this.openFrom.type='time';this.openUntil.type='time';
  details.append(field('نام کسب‌وکار',this.nameInput),field('توضیح کوتاه',this.descriptionInput),field('شروع فعالیت',this.openFrom),field('پایان فعالیت',this.openUntil));
  this.avatarEditor=new StoreAvatarEditor(api,assets,this.previewHost,onAvatarChanged,get('[data-studio-pane="avatar"]'),route=>{
    this.root.dataset.avatarMode=route?'route':'appearance';this.queuePreview();
  });
  this.avatarEditor.attachPortrait(get('.city-studio-v2__portrait'));
  // Make the avatar tray interactive before the Pixi renderer finishes initializing.
  this.avatarEditor.mount();
  const save=get('[data-studio-save]') as HTMLButtonElement;save.onclick=()=>void this.save(save);
  get('[data-studio-plans]').onclick=()=>{if(this.house){const house=this.house;if(this.close())this.onOpenPlans(house);}};
  if(isAdmin){
    const remove=document.createElement('button');remove.type='button';remove.className='city-btn city-btn--danger';
    remove.textContent='حذف مغازه';remove.onclick=()=>void this.deleteStorefront(remove);
    get('.city-studio-v2__footer').prepend(remove);
  }
  this.root.append(panel);document.body.append(this.root);this.switchTab('shop');
  this.previewResize.observe(this.previewHost);
  document.addEventListener('keydown',this.onKey);
  this.root.addEventListener('pointerdown',event=>{if(event.target===this.root)this.close();});
 }
 private switchTab(tab:'shop'|'avatar'|'details'){
  this.root.dataset.studioTab=tab;
  for(const button of this.root.querySelectorAll<HTMLButtonElement>('[data-studio-tab]'))button.setAttribute('aria-pressed',String(button.dataset.studioTab===tab));
  this.avatarEditor.showEditor(tab==='avatar');
 }
 private queuePreview(rebuild=true){
  if(rebuild)this.previewNeedsRebuild=true;
  window.clearTimeout(this.previewTimer);
  this.previewTimer=window.setTimeout(()=>{
    void this.renderPreview().catch(error=>this.showNotice(error instanceof Error?error.message:String(error),true));
  },80);
 }
 async open(house:HouseData):Promise<void>{
  if(!house.canManage)return;this.returnFocus=document.activeElement instanceof HTMLElement?document.activeElement:null;this.savedFingerprint='';const version=++this.openVersion;
  this.house=null;this.previewZoom=1;this.previewNeedsRebuild=true;this.switchTab('shop');this.avatarEditor.open(house);this.root.hidden=false;this.onVisibilityChange(true);this.root.dataset.loading='true';const saveControl=this.root.querySelector<HTMLButtonElement>('[data-studio-save]');if(saveControl)saveControl.disabled=true;document.body.classList.add('city-modal-open');this.showNotice('در حال آماده‌سازی طراح…');
  try {
  const catalog=await loadKit();if(version!==this.openVersion||this.disposed)return;
  this.house=house;this.draftModular=resolveDesign(house,catalog);this.draftPlacement=deepClone(house.placement);this.draftPlacement.components=[];
  const center=this.parcelCenter();this.draftPlacement.x=center.x;this.draftPlacement.y=center.y;
  this.draftStore=deepClone(house.store??{});this.nameInput.value=house.businessName;
  this.descriptionInput.value=String(house.store?.description??house.store?.bio??'');this.openFrom.value=house.openFrom??'';this.openUntil.value=house.openUntil??'';
  await this.modularControls.open(this.draftModular);if(version!==this.openVersion||this.disposed)return;
  this.root.hidden=false;this.notice.hidden=true;document.body.classList.add('city-modal-open');
  try {
    await this.ensurePreview();
    if(version!==this.openVersion||this.disposed)return;
    await this.renderPreview();
  } catch(error) {
    // On older iOS/Android GPUs a second Pixi context can be refused.
    // Keep the shop form usable instead of closing/crashing the whole page.
    this.showNotice('پیش‌نمایش گرافیکی در این دستگاه در دسترس نیست؛ ویرایش و ذخیره همچنان فعال است.',true);
    console.warn('Store preview could not initialize on this device',error);
  }
  this.savedFingerprint=this.fingerprint();this.root.querySelector<HTMLButtonElement>('[data-studio-close]')?.focus();
  } catch(error) {this.showNotice(error instanceof Error?error.message:'باز کردن طراح ناموفق بود.',true);throw error;}
  finally {if(version===this.openVersion){this.root.dataset.loading='false';const saveControl=this.root.querySelector<HTMLButtonElement>('[data-studio-save]');if(saveControl)saveControl.disabled=false;}}
 }
 close(force=false):boolean{
  if(!force&&!this.root.hidden&&this.savedFingerprint&&this.fingerprint()!==this.savedFingerprint&&!window.confirm('تغییرات ذخیره‌نشده کنار گذاشته شود؟'))return false;
  const wasVisible=!this.root.hidden;
  this.openVersion++;this.previewVersion++;window.clearTimeout(this.previewTimer);
  this.avatarEditor.close();this.root.hidden=true;
  this.releasePreview();
  document.body.classList.remove('city-modal-open');
  if(wasVisible)this.onVisibilityChange(false);
  this.returnFocus?.focus();return true;
 }
 private releasePreview():void{
  // Free the extra WebGL context and framebuffer immediately on close.
  const app=this.previewApp;
  const world=this.previewWorld;
  this.previewApp=null;this.previewWorld=null;this.previewSetup=null;this.previewNeedsRebuild=true;
  this.previewHost.removeAttribute('aria-busy');
  if(world)for(const child of world.removeChildren())child.destroy({children:true});
  if(app)app.destroy(true);
  this.previewHost.replaceChildren(this.previewControls);
 }
 destroy(){this.disposed=true;this.close(true);document.removeEventListener('keydown',this.onKey);this.modularControls.destroy();this.previewResize.disconnect();this.avatarEditor.destroy();this.root.remove();}
 private parcelCenter(){return {x:this.draftPlacement?.parcelBounds?.centerX??this.house?.placement.x??0,y:this.draftPlacement?.parcelBounds?.centerY??this.house?.placement.y??0};}
 private ensurePreview(){return this.previewSetup??=(this.setupPreview().catch(error=>{this.previewSetup=null;throw error;}));}
 private async setupPreview(){
  if(this.previewApp)return;
  const version=this.openVersion;
  const width=Math.max(120,this.previewHost.clientWidth);
  const height=Math.max(120,this.previewHost.clientHeight);
  const mobile=isMobileGPU();
  const app=new Application();
  await app.init({
    width,height,
    antialias:!mobile,
    autoDensity:true,
    background:'#0d1d2a',
    resolution:cityRenderResolution(width,height,'studio'),
  });
  if(this.disposed||this.root.hidden||version!==this.openVersion){
    app.destroy(true);return;
  }
  // The workshop renders on demand, not on a second continuous GPU ticker.
  app.ticker.stop();
  app.canvas.className='city-store-designer__preview-canvas';
  app.canvas.dataset.renderResolution=String(app.renderer.resolution);
  this.previewHost.replaceChildren(app.canvas,this.previewControls);
  this.avatarEditor.mount();
  const world=new Container();world.sortableChildren=true;app.stage.addChild(world);
  this.previewApp=app;this.previewWorld=world;
 }
  private async renderPreview(): Promise<void> {
    if (
      !this.previewWorld ||
      !this.previewApp ||
      !this.house ||
      !this.draftPlacement
    ) return;
    if(this.root.hidden)return;
    const previewVersion=++this.previewVersion;
    this.previewHost.setAttribute('aria-busy','true');

    const width = Math.max(120, this.previewHost.clientWidth || 960);
    const height = Math.max(120, this.previewHost.clientHeight || 640);
    // Resizing an unchanged WebGL render target forces needless allocations,
    // especially when the user only taps +/- on the preview.
    if (this.previewApp.screen.width!==width || this.previewApp.screen.height!==height)
      this.previewApp.renderer.resize(width,height);
    const previousScene=this.previewWorld.children[0];
    if(!this.previewNeedsRebuild&&previousScene instanceof Container){
      // Zoom and orientation changes only alter the camera transform.
      // Rebuilding every PNG and sign texture on each resize is expensive.
      this.positionPreviewScene(previousScene,width,height);
      return;
    }
    // Keep the previous frame on screen while the new Pixi scene is loading.
    // Swap atomically only after the replacement is ready.
    const previewPlacement = deepClone(this.draftPlacement);
    // The real city coordinates can be far away from (0,0). In the studio
    // preview everything must be rendered in plot-local space or the shop can
    // end up completely outside the preview canvas.
    const parcelCenter = this.parcelCenter();
    previewPlacement.x =
      Number(this.draftPlacement.x ?? parcelCenter.x) - parcelCenter.x;
    previewPlacement.y =
      Number(this.draftPlacement.y ?? parcelCenter.y) - parcelCenter.y;

    const draft: HouseData = {
      ...this.house,
      businessName: this.nameInput.value || this.house.businessName,
      placement: previewPlacement,
      store: deepClone(this.draftStore),
      modular: deepClone(this.draftModular),
      design: {...this.house.design,modular:deepClone(this.draftModular)},
    };
    const projection = new IsoProjection(64, 32);
    const scene = new Container();
    scene.sortableChildren = true;

    const parcel = previewPlacement.parcelBounds;
    if (parcel) {
      const halfWidth = Math.max(0.5, Number(parcel.width) / 2);
      const halfDepth = Math.max(0.5, Number(parcel.depth) / 2);
      const a = projection.worldToScreen(-halfWidth, -halfDepth);
      const b = projection.worldToScreen(halfWidth, -halfDepth);
      const c = projection.worldToScreen(halfWidth, halfDepth);
      const d = projection.worldToScreen(-halfWidth, halfDepth);
      const boundary = new Graphics()
        .poly([a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y])
        .fill({ color: 0x38bdf8, alpha: 0.055 })
        .stroke({
          color: this.isAdmin ? 0xa78bfa : 0x38bdf8,
          width: 2,
          alpha: 0.85,
        });
      boundary.zIndex = -1000000;
      scene.addChild(boundary);
    }

    const assetMap = new Map(this.assets.map((asset) => [asset.assetId, asset]));
    const house = new House(
      draft,
      projection,
      assetMap,
      (asset) => asset.sourceUrl ?? null,
    );
    await house.build();
    if(previewVersion!==this.previewVersion||this.root.hidden||!this.previewWorld||this.previewWorld.destroyed){house.destroy({children:true});scene.destroy({children:true});return;}
    const housePosition = projection.worldToScreen(
      previewPlacement.x,
      previewPlacement.y,
    );
    house.position.set(housePosition.x, housePosition.y);

    scene.addChild(house);
    for(const previous of this.previewWorld.removeChildren())previous.destroy({children:true});
    this.previewWorld.addChild(scene);
    this.previewNeedsRebuild=false;
    this.positionPreviewScene(scene,width,height);
  }

  private positionPreviewScene(scene:Container,width:number,height:number):void {
    if(!this.previewApp||!this.draftPlacement)return;
    const parcelWidth = Math.max(
      1,
      Number(this.draftPlacement.parcelBounds?.width ?? 5.5),
    );
    const parcelDepth = Math.max(
      1,
      Number(this.draftPlacement.parcelBounds?.depth ?? 5.5),
    );
    const bounds=scene.getLocalBounds();
    const fit=this.previewZoom*Math.min((width-64)/Math.max(1,bounds.width),(height-100)/Math.max(1,bounds.height),3);
    scene.scale.set(fit);
    scene.position.set(width/2-(bounds.x+bounds.width/2)*fit,height/2-(bounds.y+bounds.height/2)*fit+12);
    this.avatarEditor.updatePreview({
      width: this.previewApp.screen.width,
      height: this.previewApp.screen.height,
      sceneX: scene.position.x,
      sceneY: scene.position.y,
      scale: fit,
      parcelWidth,
      parcelDepth,
    });
    this.previewApp.render();
    this.previewHost.setAttribute('aria-busy','false');
  }

  private async deleteStorefront(button: HTMLButtonElement): Promise<void> {
    if (!this.isAdmin || !this.house) return;
    const confirmed = window.confirm(
      `مغازهٔ «${this.house.businessName}» حذف شود؟ زمین آن حذف نمی‌شود.`,
    );
    if (!confirmed) return;

    button.disabled = true;
    button.textContent = "در حال حذف…";
    try {
      await this.api.deleteStorefront(this.house.externalId);
      this.close();
      location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = "حذف مغازه";
      this.showNotice(
        error instanceof Error ? error.message : "حذف مغازه ناموفق بود.",
        true,
      );
    }
  }

  private async save(button: HTMLButtonElement): Promise<void> {
    if (!this.house || !this.draftPlacement || this.root.dataset.loading === 'true') return;
    const businessName = this.nameInput.value.trim();
    if (!businessName) {
      this.showNotice("نام کسب‌وکار الزامی است.", true);
      return;
    }

    button.disabled = true;
    button.textContent = "در حال ذخیره…";
    this.draftStore = {
      ...this.draftStore,
      description: this.descriptionInput.value.trim(),
    };

    const target = this.house;
    const version = this.openVersion;
    const payload = deepClone({
        businessName,
        openFrom: this.openFrom.value,
        openUntil: this.openUntil.value,
        avatar: this.avatarEditor.getDraft(),
        design: {
          ...(target.design ?? {}),
          store: this.draftStore,
          placement: this.draftPlacement,
          modular: this.draftModular,
        },
      });
    const fingerprint=this.fingerprint();
    try {
      const result = await this.api.updateStorefront(target.externalId, payload);
      this.onSaved(result.storefront);
      if (version === this.openVersion) {
        if(this.fingerprint()===fingerprint){this.avatarEditor.acceptSaved(result.storefront);this.savedFingerprint=this.fingerprint();}
        this.house = result.storefront;
        this.showNotice("طراحی مغازه ذخیره شد.");
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = "ذخیره تغییرات";
      if (version === this.openVersion) this.showNotice(
        error instanceof Error ? error.message : "ذخیره طراحی ناموفق بود.",
        true,
      );
    } finally {
      if(version===this.openVersion){button.disabled = false;button.textContent = "ذخیره تغییرات";}
    }
  }

  private showNotice(message: string, error = false): void {
    this.notice.hidden = false;
    this.notice.className = error
      ? "city-modal__notice city-studio-v2__notice is-error"
      : "city-modal__notice city-studio-v2__notice";
    this.notice.textContent = message;
  }
}
